import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import useModalStore from '@/stores/useModalStore';
import { ShieldCheck, AlertCircle } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { verifyCodeOptions } from '@/services/queryOptions/codeVerificationQueryOptions';
import usePartnerLastActiveProject from '@/hooks/usePartnerLastActiveProject';
import { codeVerificationKeys } from '@/constant/queryKeys';

export default function CodeVerificationModal() {
  const { codeVerification, setCodeVerification } = useModalStore();
  const { toast } = useToast();
  const { data: project } = usePartnerLastActiveProject();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [hasError, setHasError] = useState(false);

  const verifyMutation = useMutation(verifyCodeOptions());

  const handleClose = () => {
    setCodeVerification({ show: false, onVerified: undefined });
    setCode('');
    setHasError(false);
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setHasError(true);
      toast({
        title: 'Error',
        description: 'Please enter a verification code.',
        variant: 'destructive',
      });
      return;
    }

    if (!project?.id) {
      toast({
        title: 'Error',
        description: 'Project information not available.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await verifyMutation.mutateAsync({
        projectId: project.id,
        code: code.trim(),
      });

      if (result.success) {
        // Success
        setHasError(false);
        toast({
          title: 'Success',
          description: result.message,
          variant: 'default',
        });

        // Invalidate verification status cache
        queryClient.invalidateQueries({
          queryKey: codeVerificationKeys.status(project.id),
        });

        // Call the onVerified callback if provided
        if (codeVerification.onVerified) {
          codeVerification.onVerified();
        }

        // Close modal after brief delay to show success toast
        setTimeout(() => {
          handleClose();
        }, 500);
      } else {
        // API returned success: false
        setHasError(true);
        toast({
          title: 'Invalid Code',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      // Error from API (400, 500, etc.)
      setHasError(true);
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred during verification.';
      toast({
        title: 'Verification Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    // Clear error state when user starts typing again
    if (hasError) {
      setHasError(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !verifyMutation.isPending) {
      handleVerify();
    }
  };

  return (
    <Dialog open={codeVerification.show} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-secondary border border-gray-800 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShieldCheck className="h-5 w-5 text-purple-500" />
            Verification Required
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Enter the verification code to proceed with minting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="code" className="text-sm font-medium text-gray-300">
              Verification Code
            </label>
            <div className="flex gap-2">
              <Input
                id="code"
                type="text"
                placeholder="Enter code"
                value={code}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={verifyMutation.isPending}
                className={`flex-1 bg-gray-900 text-white placeholder:text-gray-500 focus-visible:ring-0 focus:outline-none focus-visible:border-purple-500 ${
                  hasError ? 'border-red-500' : ''
                }`}
                autoFocus
              />
              <Button
                onClick={handleVerify}
                disabled={verifyMutation.isPending || !code.trim()}
                className="disabled:opacity-50 text-[#FFFFFF] font-['Inter'] font-semibold "
                style={{
                    background: 'linear-gradient(0deg, #101D4A 0%, #873ED0 100%)',
                }}
              >
                {verifyMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Verifying...
                  </span>
                ) : (
                  'Verify'
                )}
              </Button>
            </div>
            {hasError && (
              <div className="flex items-start gap-2 text-sm text-red-400">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>The code you entered is invalid. Please check and try again.</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
